import React, { Component } from 'react';
import { bool, string, shape, number, object, func } from 'prop-types';
// This MultiTouch lib is used for 2-finger panning.
// which prevents user to experience map-scroll trap, while scrolling the page.
// https://github.com/mapbox/mapbox-gl-js/issues/2618
// TODO: we should add an overlay with text "use two fingers to pan".
import MultiTouch from 'mapbox-gl-multitouch';
import uniqueId from 'lodash/uniqueId';

import { circlePolyline } from '../../util/maps';
import { createMapboxMap } from '../../util/mapboxSupport';

const mapMarker = () => {
  return new window.mapboxgl.Marker();
};

const createCircleSource = (center, mapsConfig, sourceId) => {
  const path = circlePolyline(center, mapsConfig.fuzzy.offset).map(([lat, lng]) => [lng, lat]);
  return {
    type: 'geojson',
    data: {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [path],
      },
    },
  };
};

const circleLayer = (mapsConfig, layerId, sourceId) => {
  const fillOpacity = mapsConfig.fuzzy.fillOpacity !== undefined ? mapsConfig.fuzzy.fillOpacity : 0.2;

  return {
    id: layerId,
    type: 'fill',
    source: sourceId,
    paint: {
      'fill-color': mapsConfig.fuzzy.circleColor,
      'fill-opacity': fillOpacity,
    },
  };
};

const circleStrokeLayer = (mapsConfig, layerId, sourceId) => {
  const strokeOpacity = mapsConfig.fuzzy.strokeOpacity !== undefined ? mapsConfig.fuzzy.strokeOpacity : 0.5;

  return {
    id: `${layerId}_stroke`,
    type: 'line',
    source: sourceId,
    paint: {
      'line-color': mapsConfig.fuzzy.circleColor,
      'line-opacity': strokeOpacity,
      'line-width': 2,
    },
  };
};

const generateFuzzyLayerId = () => {
  return uniqueId('fuzzy_layer_');
};

class DynamicMapboxMap extends Component {
  constructor(props) {
    super(props);

    this.mapContainer = null;
    this.map = null;
    this.centerMarker = null;
    this.fuzzyLayerId = generateFuzzyLayerId();
    this.initialZoom = null;
    this.hasUserZoomed = false;
    this.zoomListener = null;

    this.updateFuzzyCirclelayer = this.updateFuzzyCirclelayer.bind(this);
    this.handleZoomChange = this.handleZoomChange.bind(this);
    this.updateMarkerAndCircleVisibility = this.updateMarkerAndCircleVisibility.bind(this);
  }
  componentDidMount() {
    const { center, zoom, mapsConfig, onZoom, showFuzzyCircleInitially } = this.props;
    const position = [center.lng, center.lat];
    this.initialZoom = zoom;

    const init = () => {
      // Never throws. Bare `new mapboxgl.Map()` threw "Failed to initialize WebGL" in
      // browsers without a GPU, and from componentDidMount that reached the route error
      // boundary and replaced the whole listing page with "Something went wrong".
      // Map.js already swaps in the static image when WebGL is known to be missing; this
      // covers the cases the pre-check cannot see. The container simply stays empty.
      const map = createMapboxMap({
        container: this.mapContainer,
        style: "mapbox://styles/mapbox/streets-v10/",
        center: position,
        zoom,
        scrollZoom: false,
      });
      if (!map) {
        return;
      }
      this.map = map;

      this.map.addControl(
        new window.mapboxgl.NavigationControl({ showCompass: false }),
        "top-left"
      );
      this.map.addControl(new MultiTouch());

      // Listen to zoom events
      this.zoomListener = () => {

        const currentZoom = this.map.getZoom();
        const hasZoomed = currentZoom - this.initialZoom > 0.1; // Small threshold for precision
        if (hasZoomed && !this.hasUserZoomed) {
          this.hasUserZoomed = true;
          this.updateMarkerAndCircleVisibility(true);
          onZoom && onZoom(true);
        } else if (!hasZoomed && this.hasUserZoomed) {
          this.hasUserZoomed = false;
          this.updateMarkerAndCircleVisibility(false);
          onZoom && onZoom(false);
        }
      };

      this.map.on('zoom', this.zoomListener);

      if (mapsConfig.fuzzy.enabled) {
        this.map.on("load", () => {
          const sourceId = `${this.fuzzyLayerId}_source`;
          this.map.addSource(sourceId, createCircleSource(center, mapsConfig, sourceId));
          this.map.addLayer(circleLayer(mapsConfig, this.fuzzyLayerId, sourceId));
          this.map.addLayer(circleStrokeLayer(mapsConfig, this.fuzzyLayerId, sourceId));

          if (showFuzzyCircleInitially) {
            // Circle is visible from the start and the exact-location marker is
            // never rendered, so zooming does not swap it in.
            return;
          }

          // Initially hide circle, show marker
          this.map.setLayoutProperty(this.fuzzyLayerId, 'visibility', 'none');
          this.map.setLayoutProperty(`${this.fuzzyLayerId}_stroke`, 'visibility', 'none');

          // Show marker initially
          this.centerMarker = new window.mapboxgl.Marker({ color: "#f2704e" })
            .setLngLat(position)
            .addTo(this.map);
        });
      } else {
        // Remove the old marker if it exists
        if (this.centerMarker) {
          this.centerMarker.remove();
        }

        // Create a new marker with an orange color
        this.centerMarker = new window.mapboxgl.Marker({ color: "#f2704e" }) // Orange
          .setLngLat(position)
          .addTo(this.map);
      }
    };

    const isReady = typeof window !== 'undefined' && window.mapboxgl && window.mapboxgl.accessToken;
    if (!isReady) {
      window.addEventListener('mapboxgl:ready', init, { once: true });
      return;
    }

    init();
  }

  componentWillUnmount() {
    if (this.map) {
      if (this.zoomListener) {
        this.map.off('zoom', this.zoomListener);
      }
      if (this.centerMarker) {
        this.centerMarker.remove();
      }
      this.centerMarker = null;
      this.map.remove();
      this.map = null;
    }
  }

  handleZoomChange(hasZoomed) {
    this.updateMarkerAndCircleVisibility(hasZoomed);
  }

  updateMarkerAndCircleVisibility(showCircle) {
    if (!this.map) return;

    const { center, mapsConfig, showFuzzyCircleInitially } = this.props;

    // The circle is already permanently visible, nothing to toggle.
    if (showFuzzyCircleInitially) return;

    const { lat, lng } = center;
    const position = [lng, lat];

    if (mapsConfig.fuzzy.enabled) {
      if (showCircle) {
        // Hide marker, show circle
        if (this.centerMarker) {
          this.centerMarker.remove();
          this.centerMarker = null;
        }
        this.map.setLayoutProperty(this.fuzzyLayerId, 'visibility', 'visible');
        this.map.setLayoutProperty(`${this.fuzzyLayerId}_stroke`, 'visibility', 'visible');
      } else {
        // Show marker, hide circle
        if (!this.centerMarker) {
          this.centerMarker = new window.mapboxgl.Marker({ color: "#f2704e" })
            .setLngLat(position)
            .addTo(this.map);
        }
        this.map.setLayoutProperty(this.fuzzyLayerId, 'visibility', 'none');
        this.map.setLayoutProperty(`${this.fuzzyLayerId}_stroke`, 'visibility', 'none');
      }
    }
  }
  componentDidUpdate(prevProps) {
    if (!this.map) {
      return;
    }

    const { center, zoom, mapsConfig } = this.props;
    const { lat, lng } = center;
    const position = [lng, lat];

    // zoom change
    if (zoom !== prevProps.zoom) {
      this.map.setZoom(this.props.zoom);
    }

    const centerChanged = lat !== prevProps.center.lat || lng !== prevProps.center.lng;

    // center marker change
    if (this.centerMarker && centerChanged) {
      this.centerMarker.setLngLat(position);
      this.map.setCenter(position);
    }

    // fuzzy circle change
    if (mapsConfig.fuzzy.enabled && centerChanged) {
      if (this.map.loaded()) {
        this.updateFuzzyCirclelayer();
      } else {
        this.map.on('load', this.updateFuzzyCirclelayer);
      }
    }

    // NOTE: mapsConfig changes are not handled
  }
  updateFuzzyCirclelayer() {
    if (!this.map) {
      // map already removed
      return;
    }
    const { center, mapsConfig } = this.props;
    const { lat, lng } = center;
    const position = [lng, lat];

    const oldSourceId = `${this.fuzzyLayerId}_source`;
    this.map.removeLayer(this.fuzzyLayerId);
    this.map.removeLayer(`${this.fuzzyLayerId}_stroke`);
    if (this.map.getSource(oldSourceId)) {
      this.map.removeSource(oldSourceId);
    }

    // We have to use a different layer id to avoid Mapbox errors
    this.fuzzyLayerId = generateFuzzyLayerId();
    const sourceId = `${this.fuzzyLayerId}_source`;
    this.map.addSource(sourceId, createCircleSource(center, mapsConfig, sourceId));
    this.map.addLayer(circleLayer(mapsConfig, this.fuzzyLayerId, sourceId));
    this.map.addLayer(circleStrokeLayer(mapsConfig, this.fuzzyLayerId, sourceId));

    this.map.setCenter(position);
  }
  render() {
    const { containerClassName, mapClassName } = this.props;
    return (
      <div className={containerClassName}>
        <div className={mapClassName} ref={el => (this.mapContainer = el)} />
      </div>
    );
  }
}

DynamicMapboxMap.defaultProps = {
  address: '',
  center: null,
  showFuzzyCircleInitially: false,
};

DynamicMapboxMap.propTypes = {
  address: string, // not used
  center: shape({
    lat: number.isRequired,
    lng: number.isRequired,
  }).isRequired,
  zoom: number.isRequired,
  mapsConfig: object.isRequired,
  onZoom: func,

  // When true (and fuzzy locations are enabled) the privacy circle is shown
  // right away instead of the exact-location marker. Defaults to false so
  // existing map instances keep the marker-until-zoom behaviour.
  showFuzzyCircleInitially: bool,
};

export default DynamicMapboxMap;
